import { motion } from "framer-motion";
import { Link } from "../lib/i18n";
import { Star, ChevronRight, Loader2, ShieldCheck } from "lucide-react";
import { fmt } from "../lib/api";
import { useSite } from "../context/SiteContext";

export function PaypalBadge({ testid = "paypal-badge", className = "" }) {
  const { payment_methods = [] } = useSite();
  if (!payment_methods.some((m) => m.id === "paypal" && m.enabled)) return null;
  return (
    <div className={`flex items-center gap-3 rounded-md border border-[#003087]/15 bg-[#003087]/[0.04] px-4 py-3 ${className}`} data-testid={testid}>
      <ShieldCheck size={18} className="shrink-0 text-[#003087]" />
      <span className="text-xs leading-snug text-encre"><b className="text-[#003087]">Paiement sécurisé PayPal</b><span className="block text-pierre">Payez avec votre compte PayPal, sans partager vos données bancaires.</span></span>
    </div>
  );
}

export const Reveal = ({ children, delay = 0, className = "", y = 24 }) => (
  <motion.div initial={{ opacity: 0, y }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }} className={className}>
    {children}
  </motion.div>
);

export const SectionHead = ({ eyebrow, title, text, action, className = "" }) => (
  <div className={`mb-10 md:mb-14 flex flex-col gap-6 md:flex-row md:items-end md:justify-between ${className}`}>
    <div className="max-w-2xl">
      {eyebrow && <p className="eyebrow mb-4">{eyebrow}</p>}
      <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold leading-[1.05]">{title}</h2>
      {text && <p className="mt-4 text-base md:text-lg text-pierre leading-relaxed">{text}</p>}
    </div>
    {action}
  </div>
);

export const Stars = ({ value = 0, size = 14 }) => (
  <span className="inline-flex gap-0.5" aria-label={`${value} sur 5`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} size={size} className={i <= Math.round(value) ? "fill-laiton text-laiton" : "text-beige"} />
    ))}
  </span>
);

export const Price = ({ price, compare, unit, big = false }) => (
  <div className="flex flex-wrap items-baseline gap-x-2">
    <span className={`font-serif font-semibold text-brun ${big ? "text-4xl" : "text-2xl"}`} data-testid="product-price">{fmt(price)}</span>
    {unit && <span className="text-sm text-pierre">/ {unit}</span>}
    {compare && compare > price && <span className="text-sm text-pierre line-through">{fmt(compare)}</span>}
  </div>
);

export const Stock = ({ stock }) => {
  const s = stock > 10 ? ["bg-[#3B5A3A]", "En stock"] : stock > 0 ? ["bg-[#B86E28]", `Plus que ${stock} disponible(s)`] : ["bg-[#9B2C2C]", "Sur commande"];
  return (
    <span className="inline-flex items-center gap-2 text-xs font-medium text-encre" data-testid="stock-badge">
      <span className={`h-2 w-2 rounded-full ${s[0]}`} />{s[1]}
    </span>
  );
};

export const Crumbs = ({ items }) => (
  <nav className="flex flex-wrap items-center gap-1.5 text-xs text-pierre" aria-label="Fil d'Ariane">
    {items.map((it, i) => (
      <span key={i} className="inline-flex items-center gap-1.5">
        {i > 0 && <ChevronRight size={12} />}
        {it.to ? <Link to={it.to} className="link-u hover:text-brun">{it.label}</Link> : <span className="text-brun">{it.label}</span>}
      </span>
    ))}
  </nav>
);

export const Spinner = ({ className = "" }) => (
  <div className={`flex justify-center py-20 ${className}`}><Loader2 className="animate-spin text-bois" /></div>
);

export const Empty = ({ title, text, action }) => (
  <div className="rounded-lg border border-dashed border-beige bg-creme/60 px-6 py-14 text-center">
    <p className="font-serif text-2xl text-brun">{title}</p>
    {text && <p className="mx-auto mt-2 max-w-md text-sm text-pierre">{text}</p>}
    {action && <div className="mt-6">{action}</div>}
  </div>
);

export const PageHero = ({ eyebrow, title, text, image, children }) => (
  <section className="relative overflow-hidden bg-ebene grain">
    {image && <img src={image} alt="" className="absolute inset-0 h-full w-full object-cover opacity-45" />}
    <div className="absolute inset-0 bg-gradient-to-r from-ebene via-ebene/80 to-transparent" />
    <div className="container-x relative py-20 md:py-28">
      <Reveal>
        {eyebrow && <p className="eyebrow mb-4 !text-laiton">{eyebrow}</p>}
        <h1 className="max-w-3xl text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.02] !text-ivoire">{title}</h1>
        {text && <p className="mt-5 max-w-xl text-base md:text-lg text-ivoire/75">{text}</p>}
        {children}
      </Reveal>
    </div>
  </section>
);

export function Markdown({ text = "" }) {
  return (
    <div className="prose-mb">
      {text.split(/\n\n+/).map((b, i) => {
        if (b.startsWith("## ")) {
          const [h, ...rest] = b.split("\n");
          return <div key={i}><h2>{h.slice(3)}</h2>{rest.length > 0 && <p>{rest.join(" ")}</p>}</div>;
        }
        if (b.startsWith("### ")) return <h3 key={i}>{b.slice(4)}</h3>;
        if (b.startsWith("- ")) return <ul key={i}>{b.split("\n").map((l, j) => <li key={j}>{l.replace(/^- /, "")}</li>)}</ul>;
        return <p key={i}>{b}</p>;
      })}
    </div>
  );
}
